/**
 * Company dataset for pre-approval quick-picks (MyGate-parity "Select company").
 * Brand colors are DATA (recognition cues on monogram tiles), not screen theming —
 * the design system's zero-hex rule governs UI chrome, not content. No raster
 * logos: tiles are coded monograms per the "coded visuals" product rule.
 */
export type CompanyCategory = 'ecom' | 'food' | 'grocery' | 'courier' | 'pharmacy' | 'gas' | 'cab' | 'services';

export type Company = {
  name: string;
  mono: string;        // 1–2 letter monogram
  color: string;       // brand tint (background)
  fg?: 'light' | 'dark';
  cat: CompanyCategory[];
};

export const COMPANIES: Company[] = [
  // e-commerce
  { name: 'Amazon', mono: 'a', color: '#232F3E', cat: ['ecom'] },
  { name: 'Flipkart', mono: 'F', color: '#2874F0', cat: ['ecom'] },
  { name: 'Myntra', mono: 'M', color: '#FF3F6C', cat: ['ecom'] },
  { name: 'Ajio', mono: 'A', color: '#2C4152', cat: ['ecom'] },
  { name: 'Meesho', mono: 'm', color: '#F43397', cat: ['ecom'] },
  { name: 'Nykaa', mono: 'N', color: '#FC2779', cat: ['ecom'] },
  { name: 'Lenskart', mono: 'L', color: '#00BAC6', cat: ['ecom'] },
  { name: 'FirstCry', mono: 'fc', color: '#C9366F', cat: ['ecom'] },
  // food
  { name: 'Swiggy', mono: 'S', color: '#FC8019', cat: ['food'] },
  { name: 'Zomato', mono: 'z', color: '#E23744', cat: ['food'] },
  { name: "Domino's", mono: 'D', color: '#006491', cat: ['food'] },
  { name: 'Pizza Hut', mono: 'PH', color: '#EE3124', cat: ['food'] },
  { name: "McDonald's", mono: 'M', color: '#DA291C', cat: ['food'] },
  { name: 'KFC', mono: 'K', color: '#A6192E', cat: ['food'] },
  { name: 'EatFit', mono: 'EF', color: '#00B5B8', cat: ['food'] },
  { name: 'Box8', mono: 'B8', color: '#E11B22', cat: ['food'] },
  // grocery / quick-commerce
  { name: 'Blinkit', mono: 'b', color: '#F8CB46', fg: 'dark', cat: ['grocery'] },
  { name: 'Zepto', mono: 'Z', color: '#7B2BF9', cat: ['grocery'] },
  { name: 'Swiggy Instamart', mono: 'IM', color: '#FC8019', cat: ['grocery'] },
  { name: 'BigBasket', mono: 'bb', color: '#84C225', cat: ['grocery'] },
  { name: 'JioMart', mono: 'J', color: '#008ECC', cat: ['grocery'] },
  { name: 'Amazon Fresh', mono: 'aF', color: '#232F3E', cat: ['grocery'] },
  { name: 'Country Delight', mono: 'CD', color: '#3C7DBC', cat: ['grocery'] },
  { name: 'Licious', mono: 'L', color: '#D11243', cat: ['grocery'] },
  { name: 'FreshToHome', mono: 'FH', color: '#2E7D32', cat: ['grocery'] },
  { name: 'Milkbasket', mono: 'mb', color: '#41B883', cat: ['grocery'] },
  // couriers
  { name: 'Delhivery', mono: 'D', color: '#1B1B1B', cat: ['courier'] },
  { name: 'Blue Dart', mono: 'BD', color: '#003DA5', cat: ['courier'] },
  { name: 'DTDC', mono: 'DT', color: '#00539F', cat: ['courier'] },
  { name: 'DHL', mono: 'DH', color: '#FFCC00', fg: 'dark', cat: ['courier'] },
  { name: 'FedEx', mono: 'Fx', color: '#4D148C', cat: ['courier'] },
  { name: 'Ecom Express', mono: 'EE', color: '#2E3192', cat: ['courier'] },
  { name: 'XpressBees', mono: 'XB', color: '#F7941D', cat: ['courier'] },
  { name: 'India Post', mono: 'IP', color: '#C8102E', cat: ['courier'] },
  { name: 'Shadowfax', mono: 'Sf', color: '#E4572E', cat: ['courier'] },
  { name: 'Porter', mono: 'P', color: '#2B4BF2', cat: ['courier'] },
  { name: 'Dunzo', mono: 'dz', color: '#00D290', cat: ['courier'] },
  // pharmacy
  { name: 'PharmEasy', mono: 'PE', color: '#10847E', cat: ['pharmacy'] },
  { name: 'Apollo Pharmacy', mono: 'Ap', color: '#0C9444', cat: ['pharmacy'] },
  { name: 'Tata 1mg', mono: '1m', color: '#FF6F61', cat: ['pharmacy'] },
  { name: 'Netmeds', mono: 'Nm', color: '#24AEB1', cat: ['pharmacy'] },
  // gas
  { name: 'Bharat Gas', mono: 'BG', color: '#FFD200', fg: 'dark', cat: ['gas'] },
  { name: 'HP Gas', mono: 'HP', color: '#002F6C', cat: ['gas'] },
  { name: 'Indane', mono: 'In', color: '#F37021', cat: ['gas'] },
  // services
  { name: 'Urban Company', mono: 'UC', color: '#1B1B1B', cat: ['services'] },
  // cabs
  { name: 'Ola', mono: 'O', color: '#A6CE39', fg: 'dark', cat: ['cab'] },
  { name: 'Uber', mono: 'U', color: '#000000', cat: ['cab'] },
  { name: 'Rapido', mono: 'R', color: '#F9C623', fg: 'dark', cat: ['cab'] },
  { name: 'BluSmart', mono: 'BS', color: '#2D46C1', cat: ['cab'] },
  { name: 'Meru', mono: 'Me', color: '#00A651', cat: ['cab'] },
  { name: 'inDrive', mono: 'iD', color: '#B7E445', fg: 'dark', cat: ['cab'] },
];

/** Delivery flow shows everything except cabs; cab flow shows cabs only. */
export function companiesFor(kind: 'delivery' | 'cab'): Company[] {
  return kind === 'cab'
    ? COMPANIES.filter((c) => c.cat.includes('cab'))
    : COMPANIES.filter((c) => !c.cat.includes('cab'));
}
